import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ChatMessage from '../components/ChatMessage';
import ChatInput from '../components/ChatInput';
import { useChat } from '../contexts/ChatContext';
import { updateSessionTimestamp } from '../lib/api/chatApi';
import useHistoricChat from '../hooks/useHistoricChat';
import useQuickTimeEvents from '../hooks/useQuickTimeEvents';
import useThinkingIndicator from '../hooks/useThinkingIndicator';
import useSafetyConcern from '../hooks/useSafetyConcern';
import useTutorial from '../hooks/useTutorial';
import useAppState from '../hooks/useAppState';
import PokketThinking from '../components/PokketThinking';
import PokketWaving from '../components/PokketWaving';
import SuggestionCards from '../components/SuggestionCards';
import ThinkingIndicatorContainer from '../components/ThinkingIndicatorContainer';
import SafetyConcernAlert from '../components/SafetyConcernAlert';
import { TutorialHighlight, TutorialOverlay } from '../components/tutorial';
import { getBillingStatus } from '../lib/api/billingApi';
import signalRService from '../lib/api/signalRService';
import { API_CONFIG, getApiUrl } from '../lib/config';
import { supabase } from '../lib/supabase/client';
import '../styles/transitions.css';
import '../styles/suggestions.css';
import '../styles/tutorial.css';
import ConfidenceDisplay from '../components/ConfidenceDisplay';

// Set to true to enable debug logs, false to disable
const DEBUG = true;

const ChatPage = ({ username }) => {
  const {
    isInitializing,
    messages,
    setMessages,
    sessionId,
    prefetchError,
    connectionEstablished,
    sendMessage,
    prefetchComplete,
    initializeChat,
    subscriptionRequired
  } = useChat();
  
  // Quick Time Events hook
  const { isQuickTimeEvent, qteOverlayText, qteSuggestions } = useQuickTimeEvents(messages);
  
  // Thinking indicator hook
  const { botIsThinking, setBotIsThinking, confidenceScore } = useThinkingIndicator(
    connectionEstablished,
    messages,
    sessionId
  );
  
  // Safety concern hook
  const { showSafetyConcern, dismissSafetyConcern } = useSafetyConcern(connectionEstablished);
  
  // App state hook - provides user details and subscription status
  const { 
    userDetails: userDetailsData, 
    isSubscriptionActive 
  } = useAppState();
  
  const [loading, setLoading] = useState(false);
  const [dailyLimitExceeded, setDailyLimitExceeded] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const [showError, setShowError] = useState(false);
  const [readyToShowFirstMessages, setReadyToShowFirstMessages] = useState(false);
  const [welcomeAnimationComplete, setWelcomeAnimationComplete] = useState(false);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [messagesTopRef, mouseTimerRef] = [useRef(null), useRef(null)];
  const isFetchingSuggestions = useRef(false);
  const errorTimerRef = useRef(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsList, setSuggestionsList] = useState(() => {
    // Try to get cached suggestions first
    const cachedData = localStorage.getItem('pokket_suggestions_cache');
    if (cachedData) {
      try {
        const cache = JSON.parse(cachedData);
        // Use cached suggestions if they exist
        if (cache.suggestions && Array.isArray(cache.suggestions) && cache.suggestions.length > 0) {
          return cache.suggestions;
        }
      } catch (e) {
        console.error('Error parsing cached suggestions:', e);
      }
    }
    
    // Fallback to default suggestions
    return [
      'Default suggestion 1 - Waiting for server responses...',
      'Default suggestion 2 - Waiting for server responses...'
    ];
  });
  
  // Map user details data from the hook to component state
  const [userDetails, setUserDetails] = useState({
    firstName: '',
    lastName: '',
    preferredName: ''
  });

  // Map subscription status from the hook to component state
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isChecking: true,
    isActive: false,
    hasBillingAccount: false
  });
  
  // Since we're disabling history completely, we can use a simplified version
  const { prevScrollHeightRef } = useHistoricChat();
  
  // Update local state when app state changes
  useEffect(() => {
    if (userDetailsData) {
      setUserDetails({
        firstName: userDetailsData.firstName || '',
        lastName: userDetailsData.lastName || '',
        preferredName: userDetailsData.preferredName || ''
      });
      
      if (DEBUG) console.log('User details loaded:', userDetailsData);
    }
  }, [userDetailsData]);
  
  // Update subscription status when it changes
  useEffect(() => {
    setSubscriptionStatus({
      isChecking: false,
      isActive: isSubscriptionActive,
      hasBillingAccount: isSubscriptionActive // Simplified, as we mainly care about active status
    });
  }, [isSubscriptionActive]);
  
  // Check if this is a first-time session based on message flags
  useEffect(() => {
    if (!sessionId || messages.length === 0) return;
    
    // Check if ALL messages have isFirstMessage flag set to true
    const allMessagesAreFirstTime = messages.every(message => message.isFirstMessage === true);
    
    if (allMessagesAreFirstTime) {
      // This is the first time the user is seeing these messages
      if (DEBUG) console.log('All messages have isFirstMessage=true, showing welcome animation');
      setShowWelcome(true);
      setUserHasInteracted(false);
      
      // Reset the first messages display flag
      setReadyToShowFirstMessages(false);
      
      // Set a timer to show the first messages after animation has started
      setTimeout(() => {
        if (DEBUG) console.log('First message display delay complete, showing messages');
        setReadyToShowFirstMessages(true);
      }, 500); // 500ms delay before showing first messages
    } else {
      // This is not the first time or we're coming back from QTE
      if (DEBUG) console.log('Not all messages are first-time, skipping welcome animation');
        setShowWelcome(false);
        setUserHasInteracted(true);
      setReadyToShowFirstMessages(true); // Show messages immediately for non-first-time sessions
    }
  }, [sessionId, messages]);
  
  // Check daily token limit - Placeholder logic removed as it depended on unused variables
  useEffect(() => {
    // TODO: Implement actual daily limit check via API if needed
    // For now, assume limit is not exceeded
    setDailyLimitExceeded(false);
  }, [sessionId]); // Re-check if session changes, or potentially based on user
  
  // Determine whether to show welcome or chat
  // Note: Now we show welcome even if there are messages, but user hasn't interacted yet
  const shouldShowWelcome = showWelcome || !subscriptionStatus.isActive || subscriptionRequired;
  
  // Check if either local subscription check or server-side check indicates subscription required
  const isSubscriptionInactive = !subscriptionStatus.isActive || subscriptionRequired;
  
  // Make sure the connection is established when page loads
  useEffect(() => {
    // If we have a chat context, make sure initialization is done
    if (!prefetchComplete && !isInitializing) {
      initializeChat();
    }
  }, [prefetchComplete, isInitializing, initializeChat]);
  
  // Ensure chat is scrolled to bottom on initial load when messages exist
  useEffect(() => {
    // This effect handles initial scrolling when revisiting or relogging
    if (messages.length > 0 && !shouldShowWelcome) {
      if (DEBUG) console.log('Setting initial scroll position to bottom');
      
      // Use a callback ref approach for messagesEndRef to ensure scroll happens immediately
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
        
        // For extra safety, scroll the container to its maximum scroll height directly
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }
    }
  }, [messages.length, shouldShowWelcome]);
  
  // Add a callback ref to ensure immediate scrolling when the chat container renders
  const scrollToBottom = () => {
    // More robust null checking
    if (chatContainerRef.current) {
      try {
        // First try to scroll the container directly
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        
        // Then use scrollIntoView as a backup method
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
        }
        
        if (DEBUG) console.log('Scrolled to bottom via enhanced method');
        
        // Add a small delay to catch any dynamically rendered content
        setTimeout(() => {
          // Check if ref is still valid before accessing properties
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        }, 50);
      } catch (error) {
        console.error('Error scrolling to bottom:', error);
      }
    }
  };
  
  // Attach the callback ref to the chat container
  const chatContainerCallback = (el) => {
    chatContainerRef.current = el;
    if (el && messages.length > 0 && !shouldShowWelcome) {
      // Immediate scroll once the container is available
      try {
        scrollToBottom();
      } catch (error) {
        console.error('Error in chat container callback scroll:', error);
      }
    }
  };
  
  // Auto-focus the input field when the component mounts
  useEffect(() => {
    if (inputRef.current && inputRef.current.focus) {
      inputRef.current.focus();
    }
  }, []);
  
  // Add new effect to handle autofocus after receiving messages
  useEffect(() => {
    // Focus input field after receiving a message (when bot is no longer thinking)
    if (!botIsThinking && !loading && inputRef.current && inputRef.current.focus && messages.length > 0) {
      inputRef.current.focus();
    }
  }, [botIsThinking, loading, messages]);
  
  // When historic messages are loaded, maintain scroll position
  useEffect(() => {
    if (chatContainerRef.current && prevScrollHeightRef.current > 0) {
      const newScrollHeight = chatContainerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeightRef.current;
      chatContainerRef.current.scrollTop = scrollDiff > 0 ? scrollDiff : 0;
      prevScrollHeightRef.current = 0;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Handle mouse movement to trigger welcome fadeout
  useEffect(() => {
    // Only set up listener if welcome is showing and transition isn't happening
    // And if the subscription is active (don't fade for inactive subscriptions)
    if (!showWelcome || isTransitioning || userHasInteracted || !subscriptionStatus.isActive) return;

    let timerActive = false;

    const handleMouseMovement = () => {
      // Set the timer only once until it completes or is cleared
      if (!timerActive) {
        timerActive = true;
        console.log('Mouse movement detected, setting 3 second timer'); // Debug log
        
        // Clear any existing timer
        if (mouseTimerRef.current) {
          clearTimeout(mouseTimerRef.current);
        }
        
        // Set a new timer for 3 seconds
        mouseTimerRef.current = setTimeout(() => {
          console.log('Timer completed, fading out welcome screen'); // Debug log
          timerActive = false;
          // Check if welcome is still showing when timer completes
          if (showWelcome && !isTransitioning) {
            handleStartConversation();
          }
        }, 4000);
      }
    };

    // Add event listener
    window.addEventListener('mousemove', handleMouseMovement);
    console.log('Added mouse movement event listener'); // Debug log
    
    // Cleanup
    return () => {
      console.log('Cleaning up mouse movement listener'); // Debug log
      window.removeEventListener('mousemove', handleMouseMovement);
      if (mouseTimerRef.current) {
        clearTimeout(mouseTimerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWelcome, isTransitioning, userHasInteracted, subscriptionStatus.isActive]);
  
  // Handle starting the conversation and fading out welcome
  const handleStartConversation = () => {
    // Only if subscription is active should we start the conversation
    if (showWelcome && subscriptionStatus.isActive) {
      // Clear any existing mouse movement timer
      if (mouseTimerRef.current) {
        clearTimeout(mouseTimerRef.current);
        mouseTimerRef.current = null;
      }
      
      setIsTransitioning(true);
      // After transition time, hide welcome completely
      setTimeout(() => {
        setShowWelcome(false);
        setIsTransitioning(false);
        setUserHasInteracted(true);
        
        // Ensure first messages are shown after welcome screen is gone
        if (!readyToShowFirstMessages) {
          setReadyToShowFirstMessages(true);
        }
      }, 400); // Transition time changed to 400ms as requested
    }
  };
  
  // Handle user input change to detect when typing starts
  const handleInputChange = (text) => {
    // If this is the first time the user types, and we're showing welcome,
    // start the transition to the main chat (only if subscription is active)
    if (showWelcome && text.length > 0 && !isTransitioning && !isSubscriptionInactive) {
      handleStartConversation();
    }
  };
  
  // Scroll to bottom when messages change or when suggestion cards become visible
  useEffect(() => {
    try {
      if (messagesEndRef.current && !shouldShowWelcome) {
        // Immediate scrolling without animation for better performance
        messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
        
        // Direct scrolling of container for consistent behavior
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }
    } catch (error) {
      console.error('Error scrolling to bottom after message change:', error);
    }
  }, [messages, shouldShowWelcome, showSuggestions]);
  
  const handleSendMessage = async (text) => {
    // Don't allow messages if subscription is inactive or if tutorial mode is active
    if (isSubscriptionInactive || isTutorialActive) {
      return;
    }
    
    if (!sessionId) {
      console.error('No active session ID');
      return;
    }
    
    // Hide suggestions immediately when user sends a message
    setShowSuggestions(false);
    
    const userMessage = {
      id: Date.now(),
      text,
      sender: 'user',
      sessionId: sessionId,
      timestamp: new Date(),
      isHistoric: false
    };
    
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    
    // Set both states to indicate waiting for response
    setLoading(true); // Controls input field disabled state
    setBotIsThinking(true); // Controls thinking indicator display
    
    if (DEBUG) console.log('Bot is thinking, waiting for response...');
    
    try {
      // Use the send message function from context
      const result = await sendMessage(text);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to send message');
      }
      
      updateSessionTimestamp();
    } catch (error) {
      console.error('Error processing message:', error);
      const fallbackBotResponse = {
        id: Date.now() + 1, // Ensure unique key
        text: `I'm sorry, I'm having trouble connecting to my brain right now. Please try again in a moment.`,
        sender: 'bot',
        sessionId: sessionId,
        timestamp: new Date(),
        isHistoric: false
      };
      // Add fallback message without removing previous user message
      setMessages((prevMessages) => [...prevMessages, fallbackBotResponse]);
      
      // Only clear thinking state on error
      setBotIsThinking(false);
    } finally {
      // Re-enable input field but don't clear botIsThinking
      // (that's now handled by the message handler)
      setLoading(false);
      
      if (DEBUG) console.log('Message sent, waiting for bot response');
    }
  };
  
  // Add function to handle suggestion clicks
  const handleSuggestionClick = (text) => {
    // Don't process clicks if tutorial is active
    if (isTutorialActive) {
      if (DEBUG) console.log('Tutorial is active, ignoring suggestion click');
      return;
    }
    
    // When a suggestion card is clicked, send it as a message
    handleSendMessage(text);
    // Hide suggestions after click
    setShowSuggestions(false);
    
    // Clear saved suggestions from localStorage when clicked
    localStorage.removeItem('pokket_suggestions_cache');
    localStorage.removeItem('pokket_show_suggestions');
  };
  
  // Function to fetch suggestion cards from API
  const fetchSuggestionCards = useCallback(async () => {
    // Skip if already fetching to prevent duplicate API calls
    if (isFetchingSuggestions.current) {
      if (DEBUG) console.log('Already fetching suggestions, skipping duplicate call');
      return;
    }
    
    // Skip API call if we're in QTE mode - QTE suggestions are handled separately
    if (isQuickTimeEvent) {
      if (DEBUG) console.log('In Quick Time Event mode, skipping suggestion API call');
      return;
    }
    
    try {
      isFetchingSuggestions.current = true;
      if (DEBUG) console.log('Fetching suggestion cards from API');
      
      // Get the session token from Supabase
      const { data } = await supabase.auth.getSession();
      const accessToken = data?.session?.access_token;
      
      const response = await fetch(getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.GET_SUGGESTION_CARDS), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch suggestion cards: ${response.status}`);
      }
      
      const suggestions = await response.json();
      console.log("✅ Received suggestion cards from API:", suggestions);
      
      // First, hide any currently showing suggestions
      setShowSuggestions(false);
      
      // Clear the current suggestions list
      // This prevents the UI from showing old suggestions during the transition
      setSuggestionsList([]);
      
      // Wait a moment to ensure the suggestions are hidden before updating with new content
      setTimeout(() => {
        // Update the state with the new suggestions
        if (Array.isArray(suggestions) && suggestions.length > 0) {
          // Set the new suggestions
          setSuggestionsList(suggestions);
          
          // Save to localStorage with timestamp
          const cache = {
            suggestions: suggestions,
            timestamp: Date.now(),
            sessionId: sessionId
          };
          localStorage.setItem('pokket_suggestions_cache', JSON.stringify(cache));
          
          // Show the suggestions after updating the content
          setTimeout(() => {
            setShowSuggestions(true);
            localStorage.setItem('pokket_show_suggestions', 'true');
            
            // Force scroll to bottom without animation when suggestions appear
            try {
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
              }
              
              // Ensure a second scroll happens after confidence display renders
              setTimeout(() => {
                try {
                  if (messagesEndRef.current) {
                    messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
                  }
                  
                  if (chatContainerRef.current) {
                    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                  }
                } catch (error) {
                  console.error('Error in second scroll after suggestions:', error);
                }
              }, 100);
            } catch (error) {
              console.error('Error scrolling after suggestions appear:', error);
            }
          }, 200);
        }
      }, 100);
    } catch (error) {
      console.error('Error fetching suggestion cards:', error);
    } finally {
      // Reset the fetching flag after a short delay to prevent rapid consecutive calls
      setTimeout(() => {
        isFetchingSuggestions.current = false;
      }, 500);
    }
  }, [sessionId, isQuickTimeEvent]);
  
  // Debug SignalR messages (keep this part)
  useEffect(() => {
    if (!connectionEstablished) return;
    
    console.log("🌟 Setting up SignalR message debugging...");
    
    // Add event listener for all messages to debug what's coming in
    const unsubscribeDebug = signalRService.onMessage((message) => {
      // Log all incoming messages to help debug
      console.log("🔍 SignalR message received:", message);
    });
    
    // Clean up
    return () => {
      unsubscribeDebug();
    };
  }, [connectionEstablished]);
  
  // Add a handler to fetch suggestion cards when a bot message is received
  useEffect(() => {
    if (!connectionEstablished) return;
    
    // Add event listener for chat messages to fetch suggestion cards
    const unsubscribeMessages = signalRService.onMessage((message) => {
      // If we receive a message from the bot, fetch suggestion cards
      if (message && !message.isFromUser) {
        if (DEBUG) console.log('Bot response received, fetching suggestion cards');
        // The fetchSuggestionCards function will handle the delay before showing cards
        fetchSuggestionCards();
      }
    });
    
    // Clean up
    return () => {
      unsubscribeMessages();
    };
  }, [connectionEstablished, fetchSuggestionCards]);
  
  // Keep this effect as a fallback for showing suggestions (no need to call API here)
  useEffect(() => {
    // Check if the last message is from the bot and the bot is not thinking
    if (
      messages.length > 0 && 
      messages[messages.length - 1].sender === 'bot' && 
      !botIsThinking
    ) {
      // Don't set showSuggestions directly here anymore
      // The API call will handle showing suggestions
    } else {
      // Hide suggestions when user sends a message or when bot is typing
      setShowSuggestions(false);
    }
  }, [messages, botIsThinking]);
  
  // Add an effect specifically for handling message length changes
  useEffect(() => {
    // If messages have been added and the welcome screen is not showing
    if (messages.length > 0 && !shouldShowWelcome) {
      // Only attempt to scroll if the ref exists
      if (messagesEndRef.current) {
        // Use direct scrolling without animation for consistent behavior
        messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
        
        if (DEBUG) console.log(`Message count changed to ${messages.length}, scrolled to bottom`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, shouldShowWelcome]);
  
  // Save suggestions to localStorage when they change
  useEffect(() => {
    // We don't need this anymore since we're using the cached approach
    // The full cache with timestamp is saved in fetchSuggestionCards
  }, [suggestionsList]);
  
  // Save showSuggestions state to localStorage
  useEffect(() => {
    localStorage.setItem('pokket_show_suggestions', JSON.stringify(showSuggestions));
  }, [showSuggestions]);
  
  // Initialize showSuggestions from localStorage
  useEffect(() => {
    // Check if there are cached suggestions and visibility state
    const savedShowSuggestions = localStorage.getItem('pokket_show_suggestions');
    const cachedData = localStorage.getItem('pokket_suggestions_cache');
    
    if (savedShowSuggestions !== null) {
      setShowSuggestions(JSON.parse(savedShowSuggestions));
    }
    
    // If we have cached suggestions but the state doesn't show them, show them
    if (cachedData && savedShowSuggestions === null) {
      try {
        const cache = JSON.parse(cachedData);
        if (cache.suggestions && Array.isArray(cache.suggestions) && cache.suggestions.length > 0) {
          setShowSuggestions(true);
        }
      } catch (e) {
        console.error('Error parsing cached suggestions during init:', e);
      }
    }
  }, []);
  
  // Reset error state when connection is established
  useEffect(() => {
    if (connectionEstablished) {
      // Clear error display if connection is successful
      setShowError(false);
      
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
        errorTimerRef.current = null;
      }
    }
  }, [connectionEstablished]);
  
  // Handle delayed error display
  useEffect(() => {
    // Clear any existing error timer
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    
    // Reset error display state when error changes
    setShowError(false);
    
    // Only set timer if there's an error to display
    if (prefetchError) {
      // Set a timer to show the error after a delay
      errorTimerRef.current = setTimeout(() => {
        // Only show error if subscriptionRequired is false or prefetchError still exists
        if (prefetchError && (!subscriptionRequired || !shouldShowWelcome)) {
          if (DEBUG) console.log('Showing error after delay:', prefetchError);
          setShowError(true);
        }
      }, 500); // 500ms delay
    }
    
    // Clean up timer on unmount or when dependencies change
    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
        errorTimerRef.current = null;
      }
    };
  }, [prefetchError, subscriptionRequired, shouldShowWelcome]);
  
  // Fetch suggestion cards on initial page load if we have messages and last is from bot
  useEffect(() => {
    // Only run once when component is mounted and after connection is established
    if (connectionEstablished && sessionId && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      
      // Don't fetch suggestions in QTE mode - QTE suggestions are fixed
      if (isQuickTimeEvent) {
        if (DEBUG) console.log('In QTE mode on page load - using QTE suggestions instead of API');
        return;
      }
      
      // Check if we have valid cached suggestions for this session
      let shouldFetchSuggestions = true;
      
      try {
        const cachedData = localStorage.getItem('pokket_suggestions_cache');
        if (cachedData) {
          const cache = JSON.parse(cachedData);
          
          // Only use cache if it matches the current session and exists
          if (cache.sessionId === sessionId && 
              cache.suggestions && 
              Array.isArray(cache.suggestions) && 
              cache.suggestions.length > 0) {
            shouldFetchSuggestions = false;
            
            // If the cache exists but suggestions aren't showing, show them
            if (!showSuggestions) {
              setShowSuggestions(true);
            }
            
            if (DEBUG) console.log('Using cached suggestion cards for session:', sessionId);
          }
        }
      } catch (e) {
        console.error('Error checking cached suggestions:', e);
      }
      
      // If the last message is from the bot and bot is not thinking, fetch cards if needed
      if (lastMessage.sender === 'bot' && 
          !botIsThinking && 
          shouldFetchSuggestions && 
          !isFetchingSuggestions.current) {
        if (DEBUG) console.log('Initial page load with bot as last message, fetching new suggestion cards');
        fetchSuggestionCards();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionEstablished, sessionId, messages.length, botIsThinking, showSuggestions, fetchSuggestionCards, isQuickTimeEvent]);
  
  // Initialize tutorial hook
  const { 
    isTutorialActive,
    isTutorialComplete,
    currentStep,
    currentStepData,
    totalSteps,
    goToNextStep,
    goToPreviousStep,
    skipTutorial,
    resetTutorial,
    setIsTutorialActive
  } = useTutorial(
    // First parameter: Is first time user (true if all messages have isFirstMessage flag)
    Boolean(messages.length > 0 && messages.every(message => message.isFirstMessage === true)),
    // Second parameter: Is welcome animation complete
    Boolean(welcomeAnimationComplete)
  );
  
  // Log tutorial state for debugging
  useEffect(() => {
    if (DEBUG) {
      console.log('Tutorial state:', { 
        isTutorialActive, 
        currentStep, 
        currentStepData,
        welcomeAnimationComplete,
        isFirstTimeUser: messages.length > 0 && messages.every(message => message.isFirstMessage === true)
      });
    }
  }, [isTutorialActive, currentStep, currentStepData, welcomeAnimationComplete, messages]);
  
  // Track welcome animation completion
  useEffect(() => {
    if (!showWelcome && !isTransitioning && userHasInteracted) {
      // Once the welcome animation has completed and user has interacted
      if (DEBUG) console.log('Welcome animation complete, ready for tutorial');
      setWelcomeAnimationComplete(true);
    }
  }, [showWelcome, isTransitioning, userHasInteracted]);
  
  // Add debug key handler to reset tutorial (Ctrl+Shift+T)
  useEffect(() => {
    if (DEBUG) {
      const handleKeyDown = (e) => {
        // Allow resetting tutorial with Ctrl+Shift+T
        if (e.ctrlKey && e.shiftKey && e.key === 'T') {
          console.log('Debug: Resetting tutorial state');
          resetTutorial();
          // Force reload to see the tutorial after reset
          window.location.reload();
        }
      };
      
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [resetTutorial]);
  
  // Function to manually activate tutorial (for testing)
  const activateTutorialManually = useCallback(() => {
    resetTutorial();
    setIsTutorialActive(true);
    console.log('Debug: Manually activating tutorial');
  }, [resetTutorial, setIsTutorialActive]);
  
  // Add an effect to ensure scrolling when confidence score changes
  useEffect(() => {
    if (confidenceScore > 0 && !botIsThinking && !shouldShowWelcome) {
      // Scroll after confidence display updates
      setTimeout(() => {
        try {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
          }
          
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
          
          if (DEBUG) console.log('Scrolled to bottom after confidence update');
        } catch (error) {
          console.error('Error scrolling after confidence update:', error);
        }
      }, 50);
    }
  }, [confidenceScore, botIsThinking, shouldShowWelcome]);
  
  // Show loading state
  if (isInitializing || subscriptionStatus.isChecking) {
    return (
      <div className="flex-1 flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  // Show error state
  if (prefetchError && showError) {
    // If this is a subscription error, show the subscription prompt instead
    if (subscriptionRequired) {
      if (DEBUG) console.log('Subscription required, showing subscription UI');
      // The welcome screen with subscription prompt will be shown via the shouldShowWelcome logic
    } else {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
          <p className="text-lg text-gray-600 mb-4">{prefetchError}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  }
  
  return (
    <div className="flex flex-col h-full max-h-screen overflow-hidden items-center">
      {/* Safety Concern Alert */}
      <SafetyConcernAlert 
        show={showSafetyConcern} 
        onDismiss={dismissSafetyConcern} 
      />
      
      {/* Tutorial Overlay - Show above all content when active */}
      <TutorialOverlay
        active={isTutorialActive}
        text={currentStepData?.text}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onNext={goToNextStep}
        onSkip={skipTutorial}
        onPrevious={goToPreviousStep}
      />
      
      {/* Highlight the sidebar if it's the current tutorial step */}
      <TutorialHighlight
        target="sidebar"
        currentTarget={currentStepData?.target}
        active={isTutorialActive}
        className="fixed left-0 top-0 h-screen z-30"
      >
        {/* No need to render sidebar here, it's managed by the Layout component */}
      </TutorialHighlight>
      
      <div className="w-full max-w-[100ch] flex flex-col flex-1 pt-[64px]">
        <div className={`flex-1 flex flex-col ${shouldShowWelcome ? 'overflow-hidden' : 'overflow-hidden'}`}>
          {shouldShowWelcome ? (
            <div className="flex-1 flex flex-col overflow-y-auto">
              <div 
                className={`flex items-center justify-center welcome-container ${!isSubscriptionInactive ? 'fade-transition' : ''} ${isTransitioning ? 'fade-out' : 'fade-in'}`} 
                onClick={!isSubscriptionInactive ? handleStartConversation : undefined}
                style={{ minHeight: '320px', paddingTop: '20px', maxHeight: '60vh' }}
              >
                <div className="text-center">
                  {/* Show waving animation for first-time users, thinking animation for inactive subscription */}
                  {!isSubscriptionInactive ? (
                    <>
                      <PokketWaving className="cursor-pointer mx-auto" />
                      <p className="text-2xl text-gray-700 mt-[10px]">
                        Hi! I'm Pokket, your mental game coach.
                      </p>
                    </>
                  ) : (
                    <>
                      <PokketThinking className="mx-auto" />
                      <div className="text-center mt-[10px]">
                        <p className="text-2xl text-gray-700 mb-3">
                          Your subscription is inactive.
                        </p>
                        <Link 
                          to="/settings?tab=subscription" 
                          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors inline-block"
                        >
                          Click here to subscribe to PokketCoach
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="flex-grow"></div>
            </div>
          ) : (
            <div 
              className={`chat-container flex-1 overflow-y-auto chat-container-wrapper ${isTransitioning ? 'opacity-0' : 'opacity-100'}`} 
              ref={chatContainerCallback}
              style={{ 
                maxHeight: 'calc(100vh - 164px)',
                scrollBehavior: 'smooth',
                overflowY: 'auto',
                paddingBottom: '10px',
                paddingTop: '10px',
                position: 'relative'
              }}
            >
              <div ref={messagesTopRef}></div>
              
              {/* Current messages - filter for first-time messages if needed */}
              {messages
                .filter(message => {
                  // If all messages have isFirstMessage flag, only show them after the delay
                  if (message.isFirstMessage && !readyToShowFirstMessages) {
                    return false; // Hide first-time messages until ready
                  }
                  return true; // Show all other messages immediately
                })
                .map((message, index) => (
                <ChatMessage
                  key={message.id || `msg-${index}-${Date.now()}`}
                  text={message.text}
                  sender={message.sender}
                  userDetails={userDetails}
                />
                ))
              }
              
              {/* Show thinking indicator only when bot is thinking */}
              <ThinkingIndicatorContainer 
                isVisible={botIsThinking}
                confidenceScore={confidenceScore}
              />
              
              {/* Display suggestion cards only when not thinking */}
              {!botIsThinking && (
                <TutorialHighlight
                  target="suggestions"
                  currentTarget={currentStepData?.target}
                  active={isTutorialActive}
                >
                  <SuggestionCards
                    suggestions={isQuickTimeEvent ? qteSuggestions : suggestionsList}
                    onSuggestionClick={handleSuggestionClick}
                    visible={isQuickTimeEvent || showSuggestions}
                    isTutorialActive={isTutorialActive}
                  />
                </TutorialHighlight>
              )}
              
              {/* Display confidence percentage - only show for regular suggestions, not QTE */}
              <TutorialHighlight
                target="confidence"
                currentTarget={currentStepData?.target}
                active={isTutorialActive}
              >
                {!botIsThinking && !isQuickTimeEvent && (
                  <ConfidenceDisplay
                    confidenceScore={confidenceScore}
                    visible={showSuggestions}
                  />
                )}
              </TutorialHighlight>
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
        
        <div className="mt-auto">
          <div className="mb-4">
            <TutorialHighlight
              target="chatInput"
              currentTarget={currentStepData?.target}
              active={isTutorialActive}
            >
              <ChatInput 
                ref={inputRef}
                onSendMessage={handleSendMessage} 
                disabled={loading || isSubscriptionInactive || isQuickTimeEvent || isTutorialActive}
                dailyLimitExceeded={dailyLimitExceeded}
                onFocus={undefined}
                onInputChange={handleInputChange}
                placeholder={
                  isSubscriptionInactive 
                    ? "Subscription required to chat with Pokket" 
                    : isQuickTimeEvent 
                      ? "Please select an option above"
                      : isTutorialActive
                        ? "Complete the tutorial to chat"
                        : undefined
                }
              />
            </TutorialHighlight>
          </div>
          
          {/* Disclaimer - always visible */}
          <div className="text-xs italic text-gray-500 mt-0 mx-4 mb-4">
            Pokket is an AI tool for improving your mental game, not a certified sport psychologist. 
            Please use with care.
            <a href="/privacy" className="text-blue-500 hover:underline"> Your privacy & Pokket.</a>
          </div>
          
          {/* Debug button for testing tutorial - only visible in development mode */}
          {DEBUG && (
            <button 
              className="fixed bottom-2 right-2 bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded opacity-50 hover:opacity-100 z-50"
              onClick={activateTutorialManually}
            >
              Activate Tutorial
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage; 